import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { supabaseTheme, supabaseThemeLight } from '../theme/supabase-theme';
import { supabase } from '../lib/supabase';

interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [userId, setUserId] = useState<string | null>(null);
  
  // Get initial theme from localStorage or default to light
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('themeMode');
    return (saved as 'light' | 'dark') || 'light';
  });

  // Listen for auth state changes
  useEffect(() => {
    // Get initial user
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) {
        setUserId(data.user.id);
        loadUserThemePreference(data.user.id);
      }
    });

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUserId(session.user.id);
        if (event === 'SIGNED_IN') {
          loadUserThemePreference(session.user.id);
        }
      } else {
        setUserId(null);
      }
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  // Load theme preference from user profile
  const loadUserThemePreference = async (uid: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('theme_preference')
        .eq('id', uid)
        .single();

      // Silently ignore if column doesn't exist (406 error)
      if (error && error.message?.includes('406')) {
        return;
      }

      if (data?.theme_preference) {
        setMode(data.theme_preference as 'light' | 'dark');
      }
    } catch (error) {
      // Silently ignore errors - theme will default to localStorage or system preference
    }
  };

  // Save theme preference to localStorage and database
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    // Also update the document meta tag for system UI elements
    document.documentElement.setAttribute('data-theme', mode);
    
    // Save to database if user is logged in
    if (userId) {
      saveThemePreference(userId, mode);
    }
  }, [mode, userId]);

  const saveThemePreference = async (uid: string, theme: 'light' | 'dark') => {
    try {
      await supabase
        .from('profiles')
        .update({ theme_preference: theme })
        .eq('id', uid);
    } catch (error) {
      // Silently ignore - column may not exist yet
    }
  };

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  // Memoize theme object to prevent unnecessary re-renders
  const theme = useMemo(() => {
    return mode === 'light' ? supabaseThemeLight : supabaseTheme;
  }, [mode]);

  const value = useMemo(() => ({
    mode,
    toggleTheme,
  }), [mode]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};